if (!customElements.get('ugc-reel')) {
  // Vertical, TikTok-style reel shown inside the UGC modal.
  // - seamless infinite loop: clones of the first/last slide bookend the track,
  //   and a settled clone is swapped for its real twin instantly
  // - only the centred slide's video plays; muted by default, tap to unmute
  // - opened at a specific index by the PDP thumbnail strip
  customElements.define(
    'ugc-reel',
    class UgcReel extends HTMLElement {
      constructor() {
        super();
        this.track = this.querySelector('.ugc-reel__track');
        this.slides = Array.from(this.querySelectorAll('.ugc-reel__slide'));
        // real slides exclude the two clones that bookend the track
        this.real_slides = this.slides.filter(
          (slide) => !slide.hasAttribute('data-clone'),
        );
        this.real_count = this.real_slides.length;
        this.is_muted = true;
        this.active_video = null;
        // real slide index currently centred; set on open, updated on scroll
        // settle. Guards ugc_scrolled from firing for the landing slide.
        this.active_index = null;
      }

      connectedCallback() {
        if (!this.track || this.real_count === 0) return;

        this.modal = this.closest('modal-dialog');
        this.modal_id = this.dataset.modal;
        this.classList.add('ugc-reel--muted');

        this.setupTriggers();
        this.setupThumbAutoplay();
        this.setupNav();
        this.setupProxyAtc();
        this.setupActiveObserver();
        this.setupProgress();
        this.setupLoop();
        this.setupTap();
        this.setupToneSync();

        // stop playback when the house modal closes
        this.on_modal_closed = () => this.pauseAll();
        document.body.addEventListener('modalClosed', this.on_modal_closed);
      }

      disconnectedCallback() {
        // the modal (with this reel) can be removed on a combined-listing swap
        if (this.on_modal_closed) {
          document.body.removeEventListener(
            'modalClosed',
            this.on_modal_closed,
          );
        }
        if (this.on_variant_change) {
          document.body.removeEventListener(
            'variantChange',
            this.on_variant_change,
          );
        }
        if (this.observer) this.observer.disconnect();
        if (this.thumb_observer) this.thumb_observer.disconnect();
        if (this.thumb_timer) clearTimeout(this.thumb_timer);
      }

      // First video thumb (marked server-side) autoplays inline once scrolled
      // into view: load the poster, then swap in the deferred video. The strip
      // lives outside the reel, so we scope the lookup by this block's modal id.
      setupThumbAutoplay() {
        const holder = document.querySelector(
          `[data-ugc-autoplay="${this.modal_id}"]`,
        );
        if (!holder) return;
        const template = holder.querySelector('template');
        if (!template) return;

        // never auto-start motion for reduced-motion users — leave poster + glyph
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          return;
        }

        // "settle, then invite": hold the poster this long AFTER the strip is in
        // view so shoppers can read price/name first, then let the video come in.
        const delay = (parseFloat(holder.dataset.autoplayDelay) || 0) * 1000;

        this.thumb_observer = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              observer.unobserve(holder);
              this.thumb_timer = setTimeout(
                () => this.playThumb(holder, template),
                delay,
              );
            });
          },
          { threshold: 0.5 },
        );
        this.thumb_observer.observe(holder);
      }

      playThumb(holder, template) {
        if (holder.dataset.loaded) return;
        const video = template.content.firstElementChild.cloneNode(true);
        holder.appendChild(video);
        holder.dataset.loaded = 'true';
        holder.classList.add('ugc__thumb-media--playing');
        // play() rejects if not ready/allowed — ignore quietly
        const played = video.play();
        if (played && played.catch) played.catch(() => {});
      }

      // PDP thumbnails open the modal and jump the reel to their index.
      setupTriggers() {
        if (!this.modal_id) return;
        const triggers = document.querySelectorAll(
          `[data-ugc-open="${this.modal_id}"]`,
        );
        triggers.forEach((trigger) => {
          trigger.addEventListener('click', () => {
            const index = parseInt(trigger.dataset.index, 10) || 0;
            this.open(index, trigger);
          });
        });
      }

      // Up/down buttons nudge the track one slide; the seamless-loop correction
      // handles wrap-around at either end.
      setupNav() {
        const prev = this.querySelector('[data-ugc-prev]');
        const next = this.querySelector('[data-ugc-next]');
        if (prev) prev.addEventListener('click', () => this.step(-1));
        if (next) next.addEventListener('click', () => this.step(1));
        // the mute shares the nav, which is excluded from tap-to-mute, so it
        // needs its own handler
        this.mute_btn = this.querySelector('[data-ugc-mute]');
        if (this.mute_btn) {
          this.mute_btn.addEventListener('click', () => this.toggleMute());
        }
      }

      step(direction) {
        const slide_h = this.track.clientHeight;
        this.track.scrollBy({ top: direction * slide_h, behavior: 'smooth' });
      }

      // The reel's main product IS the PDP product, so the dock button rebuilds
      // no purchase flow — it proxies the PDP's own primary button (simple add /
      // configurator / capp / locket). Mirror that button's label, then on click
      // close the reel and forward the click so whatever flow it drives runs
      // exactly as it would on the PDP.
      setupProxyAtc() {
        const proxy_btns = this.querySelectorAll('[data-ugc-proxy-atc]');
        if (!proxy_btns.length) return;
        // target the PDP's primary button by id (same anchor the sticky ATC
        // uses) — don't key off data-ph-source, which is for event tracking and
        // may change. Present for simple/configurator/locket; absent for
        // capp/extra-link, where the button simply won't proxy.
        const pdp_btn = document.getElementById('main_atc_button');
        proxy_btns.forEach((btn) => {
          // read the PDP button's label so the dock reads "Configure"/"Add to
          // cart"/etc. to match — no hard-coded copy that can drift per flow
          if (pdp_btn) {
            const label = pdp_btn.textContent.replace(/\s+/g, ' ').trim();
            const slot = btn.querySelector('span');
            if (label && slot) slot.textContent = label;
          }
          btn.addEventListener('click', () => {
            if (this.modal && this.modal.hide) this.modal.hide();
            if (!pdp_btn) return;
            // Hand the PDP's product-form a one-shot source so the resulting
            // add_to_cart is attributed to the reel, not the PDP. A submit
            // button's click() runs the form handler synchronously, which reads
            // + consumes this before click() returns; deleting it straight after
            // drops the flag for flows that open a modal instead of submitting
            // (configurator/locket), so it can never tag a later, unrelated add.
            (window.__phSourceOverrides || (window.__phSourceOverrides = {})).add_to_cart =
              'ugc';
            pdp_btn.click();
            delete window.__phSourceOverrides.add_to_cart;
          });
        });
      }

      open(index, opener) {
        if (!this.modal) return;
        this.modal.show(opener || this);
        // seed the centred index so the landing slide's scroll settle doesn't
        // double-count as a ugc_scrolled
        this.active_index = index;
        this.phUgc('ugc_open', { slide_index: index });
        // reflect the variant chosen before opening (event may have fired earlier)
        this.syncToneNow();
        // wait a frame so the modal is laid out before we measure/scroll
        requestAnimationFrame(() => this.scrollToIndex(index));
      }

      // Real slide i sits at child i+1 because a clone leads the track.
      scrollToIndex(index) {
        const slide_h = this.track.clientHeight;
        this.track.scrollTo({ top: (index + 1) * slide_h, behavior: 'auto' });
      }

      // Play the centred slide's video, pause the rest. Landing on an image
      // slide stops whatever video was playing so audio never bleeds through.
      setupActiveObserver() {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target.querySelector('video');
              if (!entry.isIntersecting) {
                if (video) video.pause();
                return;
              }
              if (video) {
                this.setActiveVideo(video);
              } else {
                if (this.active_video) this.active_video.pause();
                this.active_video = null;
                // image slide — hide the shared mute
                this.classList.remove('ugc-reel--has-mute');
              }
            });
          },
          { root: this.track, threshold: 0.6 },
        );
        this.real_slides.forEach((slide) => this.observer.observe(slide));
      }

      // Grow each slide's progress bar as its video plays.
      setupProgress() {
        this.slides.forEach((slide) => {
          const video = slide.querySelector('video');
          const bar = slide.querySelector('.ugc-reel__progress-bar');
          if (!video || !bar) return;
          video.addEventListener('timeupdate', () => {
            const pct = video.duration
              ? (video.currentTime / video.duration) * 100
              : 0;
            bar.style.width = `${pct}%`;
          });
        });
      }

      setActiveVideo(video) {
        if (this.active_video && this.active_video !== video) {
          this.active_video.pause();
        }
        this.active_video = video;
        video.muted = this.is_muted;
        // video slide — reveal the shared mute
        this.classList.add('ugc-reel--has-mute');
        // play() rejects if not ready/allowed — ignore quietly
        const played = video.play();
        if (played && played.catch) played.catch(() => {});
      }

      // Seamless loop: when a bookend clone settles into view, jump to its real
      // twin instantly so the scroll feels endless. Debounced scroll stands in
      // for scrollend (not supported everywhere).
      setupLoop() {
        let settle_timer = null;
        this.track.addEventListener('scroll', () => {
          if (settle_timer) clearTimeout(settle_timer);
          settle_timer = setTimeout(() => {
            this.correctLoop();
            this.reportScrollSettle();
          }, 100);
        });
      }

      // After a scroll settles, map the track position to a real slide index and,
      // if it changed, log one ugc_scrolled. correctLoop() has already swapped any
      // bookend clone for its real twin, so scrollTop sits on a real slide here.
      reportScrollSettle() {
        const slide_h = this.track.clientHeight;
        if (!slide_h) return;
        // child 0 is the leading clone, so real index = child index - 1; wrap the
        // clone positions back onto their real twins.
        const child = Math.round(this.track.scrollTop / slide_h);
        let real_index = child - 1;
        if (real_index < 0) real_index = this.real_count - 1;
        else if (real_index >= this.real_count) real_index = 0;
        if (real_index === this.active_index) return;
        this.active_index = real_index;
        this.phUgc('ugc_scrolled', { slide_index: real_index });
      }

      correctLoop() {
        const slide_h = this.track.clientHeight;
        if (!slide_h) return;
        const index = Math.round(this.track.scrollTop / slide_h);
        // child 0 = clone of last real slide -> jump to the real last slide
        if (index === 0) {
          this.track.scrollTo({
            top: this.real_count * slide_h,
            behavior: 'auto',
          });
        }
        // last child = clone of first real slide -> jump to the real first slide
        else if (index === this.real_count + 1) {
          this.track.scrollTo({ top: slide_h, behavior: 'auto' });
        }
      }

      // Tapping the reel toggles sound (first tap unmutes). Ignore taps on the
      // product dock and any links inside it.
      setupTap() {
        this.addEventListener('click', (event) => {
          if (event.target.closest('a, .ugc-reel__dock, .ugc-reel__nav')) return;
          this.toggleMute();
        });
      }

      toggleMute() {
        // only meaningful on a video slide
        if (!this.active_video) return;
        this.is_muted = !this.is_muted;
        this.active_video.muted = this.is_muted;
        this.classList.toggle('ugc-reel--muted', this.is_muted);
      }

      // Keep the dock cards in sync with the main product's metal tone. The
      // main card shows the live variant image + ATC; other cards swap to the
      // matching tone's pre-rendered image/link (falling back to their default).
      setupToneSync() {
        this.main_cards = Array.from(this.querySelectorAll('.ugc-card--main'));
        this.product_cards = Array.from(
          this.querySelectorAll('.ugc-card--product'),
        );
        if (!this.main_cards.length && !this.product_cards.length) return;

        // Only react to the MAIN product's variant. variantChange is a shared body
        // event — cart-upsell / quick-add fire it with other products' variants,
        // which would otherwise overwrite the main card with the wrong product.
        this.main_variant_ids = new Set(
          (this.dataset.variantIds || '').split(',').filter(Boolean),
        );

        this.on_variant_change = (event) =>
          this.applyTone(event.detail && event.detail.currentVariant);
        // variantChange is dispatched on body, so this works despite the modal
        // living at <body>.
        document.body.addEventListener('variantChange', this.on_variant_change);
      }

      // Read the current variant straight from the page's variant selector —
      // used on open in case the variant changed before the reel was opened.
      syncToneNow() {
        if (!this.on_variant_change) return;
        const selects = document.querySelector(
          'variant-selects, variant-radios',
        );
        if (selects && selects.currentVariant) {
          this.applyTone(selects.currentVariant);
        }
      }

      applyTone(variant) {
        if (!variant) return;
        // guard against other products' variantChange (cart-upsell, quick-add)
        if (
          this.main_variant_ids &&
          this.main_variant_ids.size &&
          !this.main_variant_ids.has(String(variant.id))
        ) {
          return;
        }

        // current metal tone = the main product's colour option value, lowercased.
        // Match by title.includes (like product-upsell.js); lowercase rather than
        // handleize so multi-word tones ("Rose Gold") still match a spaced title.
        const color_index = parseInt(this.dataset.colorIndex, 10);
        const current_color =
          color_index >= 0 && variant.options
            ? String(variant.options[color_index] || '')
                .toLowerCase()
                .trim()
            : '';

        // main product cards: live variant drives image, price, ATC id, availability
        this.main_cards.forEach((card) => {
          const img = card.querySelector('.ugc-card__img');
          if (img && variant.featured_image && variant.featured_image.src) {
            img.src = variant.featured_image.src;
          }
          const id_input = card.querySelector('[data-ugc-id]');
          if (id_input) id_input.value = variant.id;
          const price = card.querySelector('[data-ugc-price]');
          if (price) price.innerHTML = this.money(variant.price / 100);
          // proxy button (or the old inline submit) reflects availability
          const submit = card.querySelector('.ugc-card__atc');
          if (submit) {
            submit.disabled = !variant.available;
            submit.setAttribute('aria-disabled', String(!variant.available));
          }
        });

        if (!current_color) return;

        // other cards: find the variant whose title includes the tone (like
        // product-upsell.js), then swap image + point the link at that variant.
        this.product_cards.forEach((card) => {
          const data_el = card.querySelector('.ugc-card__variants');
          if (!data_el) return;
          let variants;
          try {
            variants = JSON.parse(data_el.textContent);
          } catch (e) {
            return;
          }
          const match = variants.find((v) =>
            v.title.toLowerCase().includes(current_color),
          );
          if (!match) return; // no matching tone — keep the default image

          const img = card.querySelector('.ugc-card__img');
          if (img && match.image) img.src = match.image;
          if (card.dataset.baseUrl) {
            // keep the reel-referral tag on the rewritten href so a tone change
            // before the click doesn't strip it (mirrors ugc-card.liquid).
            card.href = `${card.dataset.baseUrl}?variant=${match.id}&ph_referred=ugc`;
          }
        });
      }

      // Currency formatter matching product-upsell.js (reads <main> location/currency).
      money(amount) {
        if (!this._money) {
          const main = document.querySelector('main');
          const location = main?.dataset.location ?? 'en';
          const currency = main?.dataset.currency ?? 'AUD';
          this._money = new Intl.NumberFormat(location, {
            style: 'currency',
            currency: currency,
            currencyDisplay: 'narrowSymbol',
          });
        }
        return this._money.format(amount);
      }

      // One capture path for every ugc_* engagement event, stamping the reel's
      // shared context so new events (ugc_muted, ugc_slide_viewed, ...) inherit
      // it for free. Commerce conversions do NOT come through here — an add stays
      // the canonical add_to_cart event, tagged source 'ugc' via the proxy.
      phUgc(event, props) {
        if (typeof posthog === 'undefined') return;
        posthog.capture(
          event,
          Object.assign(
            {
              product_id: this.dataset.productId || null,
              slide_count: this.real_count,
            },
            props || {},
          ),
        );
      }

      pauseAll() {
        this.real_slides.forEach((slide) => {
          const video = slide.querySelector('video');
          if (video) video.pause();
        });
        this.active_video = null;
        this.classList.remove('ugc-reel--has-mute');
      }
    },
  );
}
